




































































import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { Cabinet, DocumentsToStore, ErrorCode, RouteRegEx, StoreConstants, TableFilterModel, TableSortModel, WorkflowQueueDataModel } from '@/types';
import MStoreTable from '@/components/shared/Table/MStoreTable.vue';
import { ComponentHelper, PinFilterHelper, RouteHelpers } from '@/mixins';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';
import { BvTableFieldArray } from 'bootstrap-vue';
import { MStoreTableUtils } from '@/utils';
import MStoreColumnFilter from '../shared/Table/MStoreColumnFilter.vue';
const UserPreferencesStore = StoreConstants.UserPreferences;

export default Vue.extend({
  name: 'OpenDocumentsToStoreAndJobsWorkflowQueue',
  mixins: [ComponentHelper, RouteHelpers, PinFilterHelper],
  props: {
    workflowQueueDataModel: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      sortOptions: [] as TableSortModel[],
      filterOptions: [] as TableFilterModel[]
    };
  },
  methods: {
    ...mapActions('workflowQueueStore', ['resetWorkflowQueueState', 'setTableStateField', 'setNextJobsList', 'setAllJobsList']),
    ...mapActions('documentViewStore', ['unloadDocument']),
    ...mapActions('userPreferencesStore', ['setUserPreferedWorkflowQueueTableOptions']),
    ...mapActions(UserPreferencesStore.ModuleName, [
      'setUserPreferedWorkflowQueueTableOptions',
      UserPreferencesStore.Actions.SetSortOptions,
      UserPreferencesStore.Actions.SetPinSort,
      UserPreferencesStore.Actions.SetFilterOptions
    ]),
    getWorkflowQueue() {
      this.$emit('get-workflow-queue', this.workflowQueueDataModel.queueId);
    },
    async onRowClicked(item: any) {
      if (item.cabinetid === Cabinet.IncomingDocuments && item.documentid && item.documenttypeid) {
        this.unloadDocument();
        this.$router.push({
          name: DocumentsToStore.DocumentsToStore.name,
          params: { documentId: item.documentid, documentTypeId: item.documenttypeid }
        });
        return;
      }
      this.$emit('get-workflow-job', item.jobid);
    },
    onTableStateFieldUpdated(this: any, field: string, value: any) {
      this.setTableStateField({
        queueId: this.workflowQueueDataModel.queueId,
        field,
        value
      });
    },
    onUserPreferedTableStateFieldUpdated(this: any, field: string, value: any) {
      this.setUserPreferedWorkflowQueueTableOptions({
        queueId: this.workflowQueueDataModel.queueId,
        field,
        value
      });
    },
    onSortingApplied(this: any) {
      this.sortItems();
    },
    updateSortItem(this: any, index: number, sortOption: TableSortModel) {
      Vue.set(this.sortOptions, index, { ...sortOption });
    },
    addSortItem(this: any, sortOption?: TableSortModel) {
      this.sortOptions.push(sortOption || { sortingKey: '', isSortingAscending: true });
    },
    deleteSortOption(this: any, index: number) {
      this.sortOptions.splice(index, 1);
    },
    resetSort(this: any) {
      this.sortOptions = [] as TableSortModel[];
      this.sortItems();
    },
    sortItems(this: any) {
      const usedFields = new Set();
      for (const option of this.sortOptions) {
        if (option.sortingKey === '') {
          SnotifyConfiguration.showError('Not all sorting options have a field selected');
          return;
        }

        if (usedFields.has(option.sortingKey)) {
          const columnDescription = this.queueColumns.find((x: any) => x.key === option.sortingKey).label;
          SnotifyConfiguration.showError(`Cannot sort on the same field (${columnDescription}) twice`);
          return;
        } else {
          usedFields.add(option.sortingKey);
        }
      }

      this.saveSortOptions();
    },
    initialise(this: any) {
      const canKeepState = this.canKeepState(RouteRegEx.WorkflowQueue);
      if (!canKeepState) {
        this.resetWorkflowQueueState(this.workflowQueueDataModel.queueId);
        this.setNextJobs(this.workflowQueueDataModel.queueData);
        this.setAllJobs(this.workflowQueueDataModel.queueData);
      }
    },
    setAllJobs(this: any, items: any) {
      this.setAllJobsList(items.map((job: any) => job.jobid));
    },
    setNextJobs(this: any, items: any) {
      this.setNextJobsList(items.map((job: any) => job.jobid));
    },
    initialiseUserSortAndFilterOptions(this: any) {
      if (!this.$router.currentRoute.params.id) {
        return;
      }

      const userSortOptions: TableSortModel[] = this.userSortOptions(this.$router.currentRoute.params.id);
      const userFilterOptions: TableFilterModel[] = this.userFilterOptions(this.$router.currentRoute.params.id);

      if (userSortOptions) {
        const validSortOptions = userSortOptions.filter((sortOption) => (this.workflowQueueDataModel as WorkflowQueueDataModel).queueColumns.map(({ key }) => key).includes(sortOption.sortingKey));

        if (userSortOptions.length !== validSortOptions.length) {
          const invalidSortKeys = userSortOptions.map((x) => x.sortingKey).filter((x) => !validSortOptions.map((sortOption) => sortOption.sortingKey).includes(x));
          SnotifyConfiguration.showInformation(`There were invalid sort keys stored: ${invalidSortKeys.join(', ')}`);
        }

        this.sortOptions = validSortOptions;
      }

      if (userFilterOptions) {
        const validFilterOptions = userFilterOptions.filter((filterOption) =>
          (this.workflowQueueDataModel as WorkflowQueueDataModel).queueColumns.map((column) => column.key).includes(filterOption.key)
        );

        if (userFilterOptions.length !== validFilterOptions.length) {
          const invalidFilterKeys = userFilterOptions.map((x) => x.key).filter((x) => !validFilterOptions.map((filterOption) => filterOption.key).includes(x));
          SnotifyConfiguration.showInformation(`There were invalid filter keys stored: ${invalidFilterKeys.join(', ')}`);
        }

        this.filterOptions = validFilterOptions;
      }
    },
    saveSortOptions(this: any) {
      const validSortOptions = this.sortOptions.filter((sortOption: TableSortModel) =>
        (this.workflowQueueDataModel as WorkflowQueueDataModel).queueColumns.map(({ key }) => key).includes(sortOption.sortingKey)
      );
      this[UserPreferencesStore.Actions.SetSortOptions]({ queueId: this.$router.currentRoute.params.id, sortOptions: validSortOptions });
    },
    updateFilter(this: any, index: number, filter: TableFilterModel) {
      Vue.set(this.filterOptions, index, filter);
    },
    addFilter(this: any) {
      this.filterOptions = [...(this.filterOptions as TableFilterModel[]), { key: '', columnFilterType: '' }] as TableFilterModel[];
    },
    clearFilters(this: any) {
      const filterFields: TableFilterModel[] = this.filterOptions;
      this.filterOptions = filterFields.map(({ key, columnFilterType }) => ({
        key,
        columnFilterType,
        value: ''
      }));
      this.updateResults(this.workflowQueueDataModel.queueData);
    },
    resetFilters(this: any) {
      this.filterOptions = [];
      this.applyFilters();
    },
    applyFilters(this: any) {
      this.saveFilters();
    },
    saveFilters(this: any) {
      const validFilterOptions = this.filterOptions.filter((filterOption: TableFilterModel) =>
        (this.workflowQueueDataModel as WorkflowQueueDataModel).queueColumns.map((column) => column.key).includes(filterOption.key)
      );
      this[UserPreferencesStore.Actions.SetFilterOptions]({ queueId: this.$router.currentRoute.params.id, filterOptions: validFilterOptions });
    },
    removeFilter(this: any, index: number) {
      this.filterOptions.splice(index, 1);
    }
  },
  computed: {
    ...mapGetters('workflowQueueStore', ['getTableStateField']),
    ...mapGetters('userPreferencesStore', ['getUserPreferedTableStateField']),
    ...mapGetters(UserPreferencesStore.ModuleName, {
      userSortOptions: UserPreferencesStore.Getters.SortOptions,
      userPinSort: UserPreferencesStore.Getters.PinSort,
      userFilterOptions: UserPreferencesStore.Getters.FilterOptions
    }),
    emptyText(this: any): string {
      return this.workflowQueueDataModel.hasNotification ? this.workflowQueueDataModel.notification : 'There are no records to show';
    },
    tableStateField(this: any) {
      return (field: string) => this.getTableStateField(this.workflowQueueDataModel.queueId, field);
    },
    userPreferedTableStateField(this: any) {
      return (field: string) => this.getUserPreferedTableStateField(this.workflowQueueDataModel.queueId, field);
    },
    emptyHtml(this: any) {
      if (this.workflowQueueDataModel.errorCode === ErrorCode.SqlTimeOutException) {
        return `<div class="alert alert-danger rounded-0 font-weight-bold">
                ${this.workflowQueueDataModel.notification}</div>`;
      }
    },
    pinSort: {
      get(this: any) {
        return this.userPinSort(this.$router.currentRoute.params.id);
      },
      set(this: any, updatedPinSort: boolean) {
        this[UserPreferencesStore.Actions.SetPinSort]({ queueId: this.$router.currentRoute.params.id, pinSort: updatedPinSort });
      }
    },
    queueData(this: any) {
      const queueData = this.workflowQueueDataModel.queueData;
      const filteredQueueData = MStoreTableUtils.applyDefaultFiltering(queueData, this.userFilterOptions(this.$router.currentRoute.params.id));
      const sortedQueueData = MStoreTableUtils.applyDefaultSorting(filteredQueueData, this.userSortOptions(this.$router.currentRoute.params.id));
      this.setNextJobs(sortedQueueData);
      return sortedQueueData;
    },
    queueColumns(): BvTableFieldArray {
      return this.workflowQueueDataModel.canOpenJobsInNewTab
        ? ([{ key: 'actions', label: '', sortable: false, thClass: 'actions-column-header', tdClass: 'actions-column-cell' }, ...this.workflowQueueDataModel.queueColumns] as BvTableFieldArray)
        : this.workflowQueueDataModel.queueColumns;
    },
    appliedSortOptions(this: any): TableSortModel[] {
      return this.userSortOptions(this.$router.currentRoute.params.id) || [];
    }
  },
  mounted() {
    this.initialise();
    this.initialiseUserSortAndFilterOptions();
  },
  components: {
    MStoreTable,
    MStoreColumnFilter
  }
});
